// src/utils/loyalty.js — loyalty points that expire.
// Points are kept as "lots": [{ pts, at }] where `at` is the time (ms) they were earned.
// A lot expires `days` days after it was earned. Redeeming uses the oldest points first.
export const DEFAULT_EXPIRY_DAYS = 30;
export const DAY_MS = 86400000;

// days from settings: not set -> 30, 0 (or blank) -> points never expire
export const expiryDaysFrom = (settings) => {
  const v = settings?.loyaltyExpiryDays;
  if (v === undefined || v === null) return DEFAULT_EXPIRY_DAYS;
  const n = Math.floor(Number(v));
  return Number.isFinite(n) && n > 0 ? n : 0;
};

// Lots from a loyalty document. An older document that only has a plain `points` number
// (saved before expiry existed) is treated as one lot earned "now", so those points get a full fresh window.
export const lotsFromDoc = (d, legacyAt = Date.now()) => {
  if (!d) return [];
  if (Array.isArray(d.lots)) {
    return d.lots.map(l => ({ pts: Number(l?.pts) || 0, at: Number(l?.at) || legacyAt })).filter(l => l.pts > 0);
  }
  const pts = Number(d.points) || 0;
  return pts > 0 ? [{ pts, at: legacyAt }] : [];
};

export const activeLots = (lots, days, now = Date.now()) =>
  days > 0 ? lots.filter(l => now < l.at + days * DAY_MS) : lots;

export const balanceOf = (lots) => lots.reduce((a, l) => a + l.pts, 0);

// take `pts` points off the OLDEST lots first
export const redeemFifo = (lots, pts) => {
  let need = Math.max(0, Number(pts) || 0);
  const out = [];
  [...lots].sort((a, b) => a.at - b.at).forEach(l => {
    if (need <= 0) { out.push(l); return; }
    const take = Math.min(l.pts, need);
    need -= take;
    if (l.pts - take > 0) out.push({ pts: l.pts - take, at: l.at });
  });
  return out;
};

// keep the list short: lots earned on the same day are merged (using the earliest time, so nothing lives longer than promised)
export const compactLots = (lots) => {
  const byDay = new Map();
  lots.forEach(l => {
    const k = new Date(l.at).toDateString();
    const cur = byDay.get(k);
    if (cur) { cur.pts += l.pts; cur.at = Math.min(cur.at, l.at); } else byDay.set(k, { pts: l.pts, at: l.at });
  });
  return [...byDay.values()].sort((a, b) => a.at - b.at);
};

// the next batch of points to expire: { at, pts } (pts = everything that expires on that same day), or null
export const nextExpiry = (lots, days, now = Date.now()) => {
  if (!(days > 0)) return null;
  const live = activeLots(lots, days, now);
  if (!live.length) return null;
  const first = live.reduce((m, l) => (l.at < m.at ? l : m));
  const day = new Date(first.at + days * DAY_MS).toDateString();
  const pts = live.filter(l => new Date(l.at + days * DAY_MS).toDateString() === day).reduce((a, l) => a + l.pts, 0);
  return { at: first.at + days * DAY_MS, pts };
};

export const fmtDate = (ms) => new Date(ms).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

// take `pts` points off the NEWEST lots first (used when a bill is deleted and its earned points are reversed)
export const removeNewestFirst = (lots, pts) => {
  let need = Math.max(0, Number(pts) || 0);
  const out = [];
  [...lots].sort((a, b) => b.at - a.at).forEach(l => {
    if (need <= 0) { out.push(l); return; }
    const take = Math.min(l.pts, need);
    need -= take;
    if (l.pts - take > 0) out.push({ pts: l.pts - take, at: l.at });
  });
  return out.sort((a, b) => a.at - b.at);
};